import { useState, useMemo, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, X, BookOpen, Calendar, Settings, AlertCircle, TrendingUp, ChevronDown, ChevronUp, CalendarClock, Plus } from 'lucide-react';
import { useLeaveRequests, useLeaveStats, useApproveLeave, useRejectLeave, useLeaveBalances, useSubmitLeave, useClearAllLeaveRequests } from '@/api/queries/leave.queries';
import { useEmployees } from '@/api/queries/employee.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';

const STATUS_TABS = ['All', 'PENDING', 'APPROVED', 'REJECTED'];

const LEAVE_TYPE_COLORS = {
  EL: '#22c55e', CL: '#3b82f6', SL: '#f59e0b',
  ML: '#ec4899', CO: '#0ea5e9', LWP: '#ef4444',
};

// ─── Sub-navigation ───────────────────────────────────────────────────────────

function SubNav() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const current   = location.pathname;

  const items = [
    { label: 'Overview',  path: '/leave',          icon: TrendingUp },
    { label: 'Requests',  path: '/leave/requests',  icon: AlertCircle },
    { label: 'Calendar',  path: '/leave/calendar',  icon: Calendar },
    { label: 'Ledger',    path: '/leave/ledger',    icon: BookOpen },
    { label: 'Policies',  path: '/settings/leave-policies', icon: Settings },
  ];

  return (
    <div className="flex gap-0.5 border-b border-[var(--border)] -mt-1 mb-1">
      {items.map(item => {
        const active = current === item.path || (item.path !== '/leave' && current.startsWith(item.path));
        return (
          <button key={item.path} onClick={() => navigate(item.path)}
            className={cn('flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-medium border-b-2 transition-all -mb-px whitespace-nowrap',
              active ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]')}>
            <item.icon size={13} />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Pending Actions Widget ───────────────────────────────────────────────────

function PendingActionsWidget({ requests, onApprove, onReject }) {
  const pending = requests.filter(r => r.status === 'PENDING');
  if (!pending.length) return null;

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle size={15} className="text-amber-500" />
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
          Pending Approvals
          <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">{pending.length}</span>
        </h3>
      </div>
      <div className="space-y-2">
        {pending.slice(0, 5).map(r => (
          <div key={r.id}
            className="flex items-center gap-3 p-2.5 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
            <Avatar name={r.employee?.name || r.employee_name} size="xs" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-[var(--text-primary)] truncate">
                {r.employee?.name || r.employee_name}
              </p>
              <p className="text-[10px] text-[var(--text-tertiary)]">
                {r.leave_type} · {r.from_date} → {r.to_date} · {r.days}d
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => onApprove(r.id)}
                className="h-7 px-2.5 text-[11px] font-medium bg-green-500 text-white rounded-[7px] hover:bg-green-600 transition-colors">
                Approve
              </button>
              <button onClick={() => onReject(r.id)}
                className="h-7 px-2.5 text-[11px] font-medium bg-red-500 text-white rounded-[7px] hover:bg-red-600 transition-colors">
                Reject
              </button>
            </div>
          </div>
        ))}
        {pending.length > 5 && (
          <p className="text-[11px] text-center text-[var(--text-tertiary)] pt-1">+{pending.length - 5} more pending</p>
        )}
      </div>
    </div>
  );
}

// ─── Leave Balance Cards ──────────────────────────────────────────────────────

function BalanceCards({ balances }) {
  if (!balances.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {balances.map((b, i) => {
        const pct = b.total ? Math.round((b.available / b.total) * 100) : 0;
        const code = b.leave_code || Object.entries({ 'Earned Leave': 'EL', 'Casual Leave': 'CL', 'Sick Leave': 'SL', 'Comp-Off': 'CO', 'Loss of Pay': 'LWP' })
          .find(([k]) => b.type?.includes(k.split(' ')[0]))?.[1] || 'EL';
        const color = LEAVE_TYPE_COLORS[code] || 'var(--accent)';

        return (
          <div key={`${b.type || code}-${i}`} className="card p-4">
            <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-2 truncate">{b.type}</p>
            <div className="flex items-end justify-between">
              <p className="text-[26px] font-black" style={{ color }}>{b.available}</p>
              <p className="text-[12px] text-[var(--text-tertiary)] mb-1">/{b.total}</p>
            </div>
            <div className="h-1.5 rounded-full bg-[var(--border)] mt-1.5">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-1.5">
              {b.used ?? 0} used{b.pending ? ` · ${b.pending} pending` : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Request Detail Panel ─────────────────────────────────────────────────────

const BAL_COLORS = {
  EL:  { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300',  border: 'border-green-200 dark:border-green-800' },
  CL:  { bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-300',    border: 'border-blue-200 dark:border-blue-800' },
  SL:  { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300',  border: 'border-amber-200 dark:border-amber-800' },
  CO:  { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' },
  LWP: { bg: 'bg-red-50 dark:bg-red-900/20',     text: 'text-red-700 dark:text-red-300',      border: 'border-red-200 dark:border-red-800' },
};

function RequestDetail({ request, onApprove, onReject }) {
  const { data, isLoading } = useLeaveBalances(request.employee?.id);
  const balances = data?.data || [];

  return (
    <div className="px-4 py-4 bg-[var(--bg-muted)] border-t border-[var(--border)]">
      <div className="flex flex-col sm:flex-row gap-5">

        {/* Employee info */}
        <div className="sm:w-64 shrink-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2.5">Employee Details</p>
          <div className="flex items-center gap-3 mb-3">
            <Avatar name={request.employee?.name} size="md" />
            <div>
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">{request.employee?.name}</p>
              {request.employee?.employee_code && (
                <p className="text-[11px] text-[var(--text-tertiary)]">{request.employee.employee_code}</p>
              )}
              {request.employee?.department && (
                <p className="text-[11px] text-[var(--text-tertiary)]">{request.employee.department}</p>
              )}
              {request.employee?.designation && (
                <p className="text-[11px] text-[var(--text-tertiary)]">{request.employee.designation}</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5 text-[12px]">
            <p className="text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">Period: </span>
              {request.from_date}{request.to_date !== request.from_date ? ` → ${request.to_date}` : ''} ({request.days} day{request.days !== 1 ? 's' : ''})
            </p>
            {request.reason && (
              <p className="text-[var(--text-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">Reason: </span>
                {request.reason}
              </p>
            )}
            <p className="text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">Applied: </span>
              {request.applied_on?.slice(0, 10)}
            </p>
            {request.source === 'ATTENDANCE_REGULARIZE' && (
              <p className="text-[11px] font-medium text-blue-600 dark:text-blue-400">Created via attendance regularization</p>
            )}
            {request.reject_reason && (
              <p className="text-red-600 dark:text-red-400">
                <span className="font-medium">Rejected: </span>{request.reject_reason}
              </p>
            )}
          </div>
          {request.status === 'PENDING' && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => onApprove(request.id)}
                className="flex-1 h-7 text-[11px] font-semibold bg-green-500 text-white rounded-[8px] hover:bg-green-600 transition-colors">
                Approve
              </button>
              <button onClick={() => onReject(request.id)}
                className="flex-1 h-7 text-[11px] font-semibold bg-red-500 text-white rounded-[8px] hover:bg-red-600 transition-colors">
                Reject
              </button>
            </div>
          )}
        </div>

        {/* Leave balance */}
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-2.5">Leave Balance</p>
          {isLoading ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">Loading balance…</p>
          ) : balances.length === 0 ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">No balance data available</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {balances.map((b, i) => {
                const code = b.leave_code || 'EL';
                const c = BAL_COLORS[code] || BAL_COLORS.EL;
                const pct = b.total ? Math.round((b.available / b.total) * 100) : 0;
                return (
                  <div key={`${code}-${i}`} className={`rounded-[10px] border p-2.5 ${c.bg} ${c.border}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wide ${c.text}`}>{code}</p>
                    <p className={`text-[22px] font-black leading-tight ${c.text}`}>{b.available}</p>
                    <div className="h-1 rounded-full bg-white/40 dark:bg-black/20 mt-1 mb-1">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'currentColor', opacity: 0.6 }} />
                    </div>
                    <p className="text-[9px] text-[var(--text-tertiary)]">{b.used ?? 0} used of {b.total}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Requests Table ───────────────────────────────────────────────────────────

function RequestsTable({ requests, onApprove, onReject }) {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <>
      {/* Desktop */}
      <div className="card p-0 overflow-x-auto hidden sm:block">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-[var(--border)]">
              {['Employee', 'Type', 'Duration', 'Reason', 'Applied On', 'Status', 'Actions', ''].map(h => (
                <th key={h} className="text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-4 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {requests.map(r => (
              <Fragment key={r.id}>
                <tr
                  onClick={() => toggle(r.id)}
                  className="hover:bg-[var(--bg-hover)] transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={r.employee?.name || r.employee_name} size="sm" />
                      <div>
                        <p className="text-[12px] font-semibold text-[var(--text-primary)]">{r.employee?.name || r.employee_name}</p>
                        <p className="text-[10px] text-[var(--text-tertiary)]">{r.employee?.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: LEAVE_TYPE_COLORS[Object.entries({ EL: 'Earned', CL: 'Casual', SL: 'Sick', ML: 'Maternity', CO: 'Comp', LWP: 'Loss' }).find(([, v]) => r.leave_type?.includes(v))?.[0] || 'EL'] || '#6b7280' }} />
                      <span className="text-[var(--text-secondary)]">{r.leave_type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-[var(--text-primary)]">{r.from_date} → {r.to_date}</p>
                    <p className="text-[10px] text-[var(--text-tertiary)]">{r.days} day{r.days !== 1 ? 's' : ''}</p>
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)] max-w-[180px] truncate">{r.reason}</td>
                  <td className="px-4 py-3 text-[var(--text-tertiary)]">{r.applied_on?.slice(0, 10)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {r.status === 'PENDING' && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => onApprove(r.id)}
                          className="p-1.5 rounded-[6px] text-green-600 hover:bg-green-50 transition-colors"><Check size={14} /></button>
                        <button onClick={() => onReject(r.id)}
                          className="p-1.5 rounded-[6px] text-red-500 hover:bg-red-50 transition-colors"><X size={14} /></button>
                      </div>
                    )}
                    {r.status !== 'PENDING' && r.approver && (
                      <p className="text-[10px] text-[var(--text-tertiary)]">by {r.approver?.name || r.approver}</p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[var(--text-tertiary)]">
                    {expandedId === r.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                  </td>
                </tr>
                {expandedId === r.id && (
                  <tr key={`${r.id}-detail`}>
                    <td colSpan={8} className="p-0">
                      <RequestDetail request={r} onApprove={onApprove} onReject={onReject} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {requests.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[13px] text-[var(--text-tertiary)]">No requests found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="sm:hidden space-y-3">
        {requests.map(r => (
          <div key={r.id} className="card overflow-hidden">
            <div className="p-4 space-y-3" onClick={() => toggle(r.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar name={r.employee?.name || r.employee_name} size="sm" />
                  <div>
                    <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.employee?.name || r.employee_name}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)]">{r.employee?.department}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status} />
                  {expandedId === r.id ? <ChevronUp size={13} className="text-[var(--text-tertiary)]"/> : <ChevronDown size={13} className="text-[var(--text-tertiary)]"/>}
                </div>
              </div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-[var(--text-secondary)]">{r.leave_type} · {r.days}d</span>
                <span className="text-[var(--text-tertiary)]">{r.from_date}</span>
              </div>
            </div>
            {expandedId === r.id && (
              <RequestDetail request={r} onApprove={onApprove} onReject={onReject} />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Quick Mark Leave Form ────────────────────────────────────────────────────

function QuickMarkLeave({ forceOpen, onForceClose }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  const handleClose = () => { setOpen(false); onForceClose?.(); };
  const [empSearch, setEmpSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [leaveType, setLeaveType] = useState('EL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');

  const { data: empResp } = useEmployees({ limit: 200 });
  const employees = empResp?.data || [];
  const submitLeave = useSubmitLeave();
  const today = new Date().toISOString().slice(0, 10);

  const filteredEmps = empSearch.length >= 2
    ? employees.filter(e => {
        const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
        return name.includes(empSearch.toLowerCase()) || (e.employee_code || '').toLowerCase().includes(empSearch.toLowerCase());
      }).slice(0, 6)
    : [];

  const LEAVE_TYPE_TO_API = { EL: 'EARNED_LEAVE', CL: 'CASUAL_LEAVE', SL: 'SICK_LEAVE' };

  const handleSubmit = () => {
    if (!selectedEmp || !fromDate || !toDate) return;
    const days = Math.max(1, Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1);
    submitLeave.mutate({
      employee_id: selectedEmp.id,
      employee_name: `${selectedEmp.first_name} ${selectedEmp.last_name || ''}`.trim(),
      employee_code: selectedEmp.employee_code || '',
      department: selectedEmp.department?.name || '',
      leave_type: LEAVE_TYPE_TO_API[leaveType] || 'EARNED_LEAVE',
      from_date: fromDate,
      to_date: toDate,
      days,
      reason: reason || 'HR marked',
      source: 'HR_MARKED',
    }, {
      onSuccess: () => {
        handleClose();
        setSelectedEmp(null);
        setEmpSearch('');
        setFromDate('');
        setToDate('');
        setReason('');
      },
    });
  };

  if (!isOpen) {
    return (
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-[10px] border border-dashed border-[var(--border)] text-[12px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] hover:border-[var(--accent)] transition-all">
        <Plus size={13} /> Quick Mark Leave
      </button>
    );
  }

  return (
    <div className="card p-4 border-[var(--accent)]/20 bg-[var(--accent)]/3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <CalendarClock size={14} className="text-[var(--accent)]" /> Mark Leave
        </h3>
        <button onClick={handleClose} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]"><X size={14}/></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Employee picker */}
        <div className="relative sm:col-span-2">
          <label className="block text-[11px] font-semibold text-[var(--text-tertiary)] mb-1">Employee</label>
          {selectedEmp ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)]">
              <span className="text-[13px] text-[var(--text-primary)] flex-1">{selectedEmp.first_name} {selectedEmp.last_name} ({selectedEmp.employee_code})</span>
              <button onClick={() => setSelectedEmp(null)} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]"><X size={12}/></button>
            </div>
          ) : (
            <>
              <input
                value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                placeholder="Type name or code..."
                className="w-full px-3 py-2 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
              />
              {filteredEmps.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-[8px] shadow-lg overflow-hidden">
                  {filteredEmps.map(e => (
                    <button key={e.id} onClick={() => { setSelectedEmp(e); setEmpSearch(''); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--bg-hover)] transition-colors">
                      <span className="text-[12px] font-medium text-[var(--text-primary)]">{e.first_name} {e.last_name}</span>
                      <span className="text-[10px] text-[var(--text-tertiary)]">{e.employee_code} · {e.department?.name || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Leave type */}
        <div>
          <label className="block text-[11px] font-semibold text-[var(--text-tertiary)] mb-1">Leave Type</label>
          <div className="flex gap-1.5">
            {[['EL','Earned'],['CL','Casual'],['SL','Sick']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setLeaveType(k)}
                className={cn('flex-1 py-2 rounded-[8px] text-[11px] font-semibold border-2 transition-all',
                  leaveType === k ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]' : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--accent)]/40')}>
                {k}
              </button>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-semibold text-[var(--text-tertiary)] mb-1">From</label>
            <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); if (!toDate) setToDate(e.target.value); }} max={today}
              className="w-full px-2.5 py-2 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)] text-[12px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"/>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--text-tertiary)] mb-1">To</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} min={fromDate} max={today}
              className="w-full px-2.5 py-2 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)] text-[12px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"/>
          </div>
        </div>

        {/* Reason */}
        <div className="sm:col-span-2">
          <label className="block text-[11px] font-semibold text-[var(--text-tertiary)] mb-1">Reason (optional)</label>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Medical, personal..."
            className="w-full px-3 py-2 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"/>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-[8px] border border-[var(--border)] text-[12px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={!selectedEmp || !fromDate || !toDate || submitLeave.isPending}
          className="px-4 py-2 rounded-[8px] bg-[var(--accent)] text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition-all">
          {submitLeave.isPending ? 'Submitting...' : 'Submit Leave'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LeavePage() {
  const [statusFilter, setStatusFilter] = useState('All');
  const [markLeaveOpen, setMarkLeaveOpen] = useState(false);

  const { data: requestsResp } = useLeaveRequests({ status: statusFilter === 'All' ? undefined : statusFilter });
  const allRequests = requestsResp?.data || [];

  const { data: statsResp } = useLeaveStats();
  const leaveBalances = statsResp?.data || [];

  const approveLeave   = useApproveLeave();
  const rejectLeave    = useRejectLeave();
  const clearAllLeaves = useClearAllLeaveRequests();

  const pendingCount = useMemo(() => allRequests.filter(r => r.status === 'PENDING').length, [allRequests]);

  const handleApprove = (id) => {
    approveLeave.mutate({ id }, { onSuccess: () => toast.success('Leave approved') });
  };

  const handleReject = (id) => {
    rejectLeave.mutate({ id, reason: 'Rejected by manager' }, { onSuccess: () => toast.success('Leave rejected') });
  };

  return (
    <div className="flex flex-col gap-4 p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary)]">Leave Management</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
            {pendingCount > 0
              ? <span className="text-amber-600 font-medium">{pendingCount} request{pendingCount !== 1 ? 's' : ''} pending approval</span>
              : 'All requests processed'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {allRequests.length > 0 && (
            <button
              onClick={() => { if (window.confirm('Remove ALL leave requests? This cannot be undone.')) clearAllLeaves.mutate(undefined, { onSuccess: () => toast.success('All leave requests cleared') }); }}
              disabled={clearAllLeaves.isPending}
              className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-red-300 text-red-600 text-[12px] font-semibold hover:bg-red-50 transition-colors whitespace-nowrap shrink-0 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <X size={13} /> Clear All
            </button>
          )}
          <button
            onClick={() => setMarkLeaveOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white text-[12px] font-semibold hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
          >
            <CalendarClock size={13} /> Mark Leave
          </button>
        </div>
      </div>

      {/* Sub-navigation */}
      <SubNav />

      {/* Leave balance overview */}
      <BalanceCards balances={leaveBalances} />

      {/* Quick Mark Leave */}
      <QuickMarkLeave forceOpen={markLeaveOpen} onForceClose={() => setMarkLeaveOpen(false)} />

      {/* Pending actions widget */}
      <PendingActionsWidget requests={allRequests} onApprove={handleApprove} onReject={handleReject} />

      {/* Status filter tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {STATUS_TABS.map(t => (
          <button key={t} onClick={() => setStatusFilter(t)}
            className={cn('h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors',
              statusFilter === t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
            {t === 'All' ? 'All Requests' : t}{t === 'PENDING' ? ` (${pendingCount})` : ''}
          </button>
        ))}
      </div>

      <RequestsTable requests={allRequests} onApprove={handleApprove} onReject={handleReject} />
    </div>
  );
}
